import React, {useEffect, useState} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {
  CurrentUserSelector,
  subscribeToCurrentUser,
} from 'app/current-user/api';
import {StartFreeTrialFloatingButton} from 'app/demo/components/StartFreeTrialFloatingButton';
import {StartTrialModalWithContext} from 'app/demo/components/StartTrialModal';
import {KeyboardShortcutsHelpModal} from 'app/general/shortcuts/KeyboardShortcutsHelpModal';
import {KeyboardShortcutsProvider} from 'app/general/shortcuts/KeyboardShortcutsProvider';
import {ErrorBoundary} from 'app/general/structures/ErrorBoundary';
import {FlexPanel} from 'app/general/structures/FlexPanel';
import {ConnectedSweetLoadingBar} from 'app/general/sweet-loading-bar';
import {PageHeadNotificationsSyncer} from 'app/notifly/components/PageHeadNotificationsSyncer';
import {
  accountSettingsSelector,
  subscribeToAccountSettings,
} from 'app/settings/api';
import {loadStatefromLocalStorage} from 'app/store/local-storage';
import {ToastNotificationsContainer} from 'app/toasts/components/ToastNotificationsContainer';
import {DEFAULT_LANG} from 'app/utils/constants';
import {setLocale} from 'app/utils/i18n';
import {hideLoadingAnimation} from 'app/utils/loading-animation';
import {useAsyncActions} from 'app/utils/redux';

interface Props {
  children: React.ReactNode;
}

export function DemoBasePage({children}: Props): JSX.Element {
  const appData = useSelector(bootstrapDataSelector);

  const actions = useAsyncActions({
    subscribeToAccountSettings,
    subscribeToCurrentUser,
    loadStatefromLocalStorage,
  });

  useEffect(() => {
    actions.subscribeToAccountSettings();
    actions.subscribeToCurrentUser();
  }, []);

  const [readyToMount, setReadyToMount] = useState(false);

  useEffect(() => {
    if (appData.ready && !readyToMount) {
      //eslint-disable-next-line no-underscore-dangle
      window.__getSweetProcessBundlesPublicPath__ = () =>
        `${appData.STATIC_URL}${BUNDLES_PATH}`;

      actions.loadStatefromLocalStorage(appData.user_id, appData.account_id);

      setLocale(appData.language)
        .then((data) => {
          setReadyToMount(true);
          setTimeout(hideLoadingAnimation, 100);
          return data;
        })
        .catch((e) => {
          throw e;
        });
    }
  }, [appData.user_id, appData.account_id]);

  return (
    <KeyboardShortcutsProvider>
      <StartTrialModalWithContext>
        <StartFreeTrialFloatingButton />
        <FlexPanel
          type="vertical"
          secondaryZindex="2"
          mainZindex="1"
          secondary={
            <StyledNotificationsDiv>
              <ErrorBoundary name="notifications-banner">
                <ConnectedSweetLoadingBar />
              </ErrorBoundary>
            </StyledNotificationsDiv>
          }
        >
          {readyToMount ? children : null}
        </FlexPanel>
        <ErrorBoundary name="modals">
          <PageHeadNotificationsSyncer />
          <ToastNotificationsContainer />
          <KeyboardShortcutsHelpModal />
        </ErrorBoundary>
      </StartTrialModalWithContext>
    </KeyboardShortcutsProvider>
  );
}

const StyledNotificationsDiv = styled.div`
  > div:not(:first-child) {
    border-top: 1px solid rgba(0, 0, 0, 0.2);
  }
`;

const bootstrapDataSelector = createSelector<
  [typeof CurrentUserSelector, typeof accountSettingsSelector],
  | {ready: false; user_id: null; account_id: null}
  | {
      ready: true;
      user_id: number;
      email: string;
      account_id: number;
      account_email: string;
      STATIC_URL: string;
      language: string;
      vapid_application_server_key: string;
      is_email_verified: boolean;
    }
>([CurrentUserSelector, accountSettingsSelector], (userData, settings) => {
  if (userData.isSuccess && settings.isSuccess) {
    return {
      ready: true,
      user_id: userData.data.id,
      email: userData.data.email,
      language: userData.data.language || DEFAULT_LANG,
      is_email_verified: userData.data.is_email_verified,
      account_id: settings.data.account_id,
      account_email: settings.data.account_email,
      STATIC_URL: settings.data.STATIC_URL,
      vapid_application_server_key: settings.data.vapid_application_server_key,
    };
  }

  return {ready: false, user_id: null, account_id: null};
});
